import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
// ANT Design
import DuotoneLightIcon from '../common/DuotoneLightIcon';

// RichEditor
import { handleAlignmentChange, toggleInlineStyle, removeInlineStyle, AddVariableInContentTextRichEditor, handleImagePositionInText, applyTextColor } from '../RichEditor/utils';

import {
  CharacterMetadata,
  ContentBlock,
  ContentState,
  EditorState, RichUtils,
} from 'draft-js';
import { useStore } from '../../store/store';
import {
  FONT_SIZES,
} from '../../constants/constants';

// common components
import '../common/Utils.css';

import { iconButtonStyle } from '../../utils/styles';
import { Separator } from '../Layout/Separator';
import { NavbarButton } from '../Layout/NavbarButton';
import ColorControl from './ColorControl';
import HighlightControl from './HighlightControl';
import FontSizeControl from './FontSizeControl';
import FontFamilyControl from './FontFamilyControl';
import NavbarSelector from '../Layout/NavbarSelector';
import { CaretDownOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import HideInFormSwitcher from '../common/HideInFormSwitcher';
import VariableSwitcher from '../common/VariableSwitcher';
import InsertionControls from '../common/InsertionControls';
import KeepTooltip from '../common/KeepTooltip';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';

export const setDefaultInlineStyleIfNeeded = (
  currentEditorState,
  removedProperty,
  defaultProperty,
  prefix = ''
) => {
  let nextContentState = currentEditorState.getCurrentContent();
  const blocks = [];
  nextContentState.getBlockMap().forEach((block) => {
    const characterList = block.getCharacterList().map((character) => {
      if (character.hasStyle(`${prefix}${removedProperty}`)) {
        return CharacterMetadata.removeStyle(
          character,
          `${prefix}${removedProperty}`
        );
      } else {
        return character;
      }
    });
    blocks.push(
      new ContentBlock({
        key: block.getKey(),
        text: block.getText(),
        type: block.getType(),
        depth: block.getDepth(),
        data: block.getData(),
        characterList,
      })
    );
  });

  return EditorState.push(
    currentEditorState,
    ContentState.createFromBlockArray(blocks),
    'change-inline-style'
  );
};

// Gras / italique / souligné que le rendu impose déjà au texte (style de
// texte, titre de volet...), sous forme de styles inline Draft : la barre les
// montre actifs comme ceux posés sur la sélection.
export const imposedInlineStyles = ({ fontWeight, fontStyle, textDecoration } = {}) => [
  (fontWeight === 'bold' || Number(fontWeight) >= 600) && 'BOLD',
  fontStyle === 'italic' && 'ITALIC',
  typeof textDecoration === 'string' && textDecoration.includes('underline') && 'UNDERLINE',
].filter(Boolean);

// Bouton actif de la barre flottante. Sur son fond marine, la seule couleur de
// l'icône (noir / marine) ne permettait pas de savoir ce qui était appliqué.
const FLOATY_ACTIVE_BUTTON_STYLE = {
  backgroundColor: 'rgb(234, 246, 246)',
  borderColor: '#1f7a7a',
};

const TextStylingComponent = ({
  boxId,
  updateBox,
  // Alignement rendu : celui de la box, sinon celui de son style de texte.
  boxAlignement,
  // Alignement rangé ailleurs que dans la box (titre de colonne de tableau).
  onAlignmentChange,
  editorState,
  setEditorState,
  updateBoxOnStylingChange,
  defaultFontSize,
  defaultFontFamily,
  defaultColor,
  // Styles inline déjà imposés par le rendu (cf. imposedInlineStyles).
  defaultInlineStyles = [],
  textStyle,
  hasAlignement = false,
  hasForm = false,
  hasList = true,
  hasSelectionStyle = true,
  hasFontSize = true,
  setDefaultSelection,
  hasVariables = false,
  colors,
  wrap = false,
  /**
   * Place des sections dans le panel de droite. Les box à plusieurs textes
   * (Volet, Carte info) montent et démontent cette barre au gré du texte
   * actif : sans un ordre figé, elle sautait du haut vers le bas du panel au
   * premier changement de texte, l'ordre de montage faisant foi.
   */
  panelOrder = 0
}) => {
  const { t } = useTranslation();
  const configuration = useStore((state) => state.configuration);

  // État pour gérer quel dropdown est ouvert
  const [openDropdown, setOpenDropdown] = useState(null); // 'color', 'highlight', 'fontSize' ou null

  // Réinitialiser l'état openDropdown quand l'editorState change
  useEffect(() => {
    setOpenDropdown(null);
  }, [editorState]);

  const toggleBlockType = (blockType) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };


  const handleAddAt = () => {
    const newEditorState = AddVariableInContentTextRichEditor(editorState, ' @');
    setEditorState(newEditorState);
  };

  const handleAddImageVariable = () => {
    const newEditorState = AddVariableInContentTextRichEditor(editorState, ' /');
    setDefaultSelection("img")
    setEditorState(newEditorState);
  }

  // Label de champ du panel (cf. NavbarTable « Style du tableau »).
  const fieldLabelStyle = {
    fontSize: 12,
    fontWeight: 500,
    color: 'rgba(11, 34, 57, 0.65)',
    whiteSpace: 'nowrap',
  };

  const currentInlineStyle = editorState.getCurrentInlineStyle(editorState.getSelection());
  const isInlineStyleActive = (inlineStyleName) =>
    currentInlineStyle.has(inlineStyleName) || defaultInlineStyles.includes(inlineStyleName);

  const textStyleButtons = [
    {
      title: t('bold'),
      textContent: 'G',
      style: { fontWeight: 'bold', fontFamily: 'monospace' },
      active: isInlineStyleActive('BOLD'),
      inlineStyleName: 'BOLD'
    },
    {
      title: t('italic'),
      textContent: 'I',
      style: { fontStyle: 'italic', fontFamily: 'monospace' },
      active: isInlineStyleActive('ITALIC'),
      inlineStyleName: 'ITALIC'
    },
    {
      title: t('underline'),
      textContent: 'S',
      style: { textDecoration: 'underline', fontFamily: 'monospace' },
      active: isInlineStyleActive('UNDERLINE'),
      inlineStyleName: 'UNDERLINE'
    }
  ];

  const imagePositionOptions = [
    { value: "start", label: <i class="fa-duotone fa-solid fa-objects-align-top"></i> },
    { value: "center", label: <i class="fa-duotone fa-solid fa-objects-align-center-vertical"></i> },
    { value: "end", label: <i class="fa-duotone fa-solid fa-objects-align-bottom"></i> }
  ]

  const toggleInlineState = (inlineStyleName) => (_e) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyleName))
  }

  const applyFontSize = (toggledFontSize) => {
    const newEditorState = toggleInlineStyle({
      selectedProperty: `FONT_SIZE_${toggledFontSize}`,
      prefix: 'FONT_SIZE_',
      editor: editorState
    })
    setEditorState(newEditorState);
    updateBoxOnStylingChange(newEditorState)
  };
  const applyColor = (toggledColor) => {
    const newEditorState = applyTextColor({ color: toggledColor, colors, editor: editorState });
    setEditorState(newEditorState);
    updateBoxOnStylingChange(newEditorState)
  };
  const applyHighlightcolor = (toggledHighlightColor) => {
    const newEditorState = toggledHighlightColor === "transparent"
      ? removeInlineStyle({
        prefix: 'HIGHLIGHT_',
        editor: editorState
      })
      : toggleInlineStyle({
        selectedProperty: `HIGHLIGHT_${toggledHighlightColor}`,
        prefix: 'HIGHLIGHT_',
        editor: editorState
      })
    setEditorState(newEditorState)
    updateBoxOnStylingChange(newEditorState)
  };
  const applyFontFamily = (toggledFontFamily) => {
    const newEditorState = toggleInlineStyle({
      selectedProperty: `FONT_FAMILY_${toggledFontFamily.replace(/\s+/g, "_")}`,
      prefix: 'FONT_FAMILY_',
      editor: editorState
    })
    setEditorState(newEditorState);
    updateBoxOnStylingChange(newEditorState)
  };

  const hasRow1Content = hasVariables || hasAlignement || hasList;
  // Barre flottante sur canvas (wrap=false): 2 lignes fixes, jamais de wrap,
  // séparateurs verticaux blancs, pensées pour une barre large. Panel de
  // droite (wrap=true, colonne étroite): sections empilées (Affichage / Texte /
  // Insertion) sans cadre, boutons de choix (alignement/gras-italique-souligné)
  // étirés en largeur égale, état actif marqué par une bordure orange.
  const groupStyle = wrap ? { flex: 1 } : undefined;
  // État actif d'un bouton de choix : bordure accent dans le panel, fond
  // pastel dans la barre flottante.
  const choiceButtonProps = (isActive) => (wrap
    ? { active: isActive, buttonStyle: groupStyle }
    : { buttonStyle: isActive ? FLOATY_ACTIVE_BUTTON_STYLE : undefined });

  // Groupes de contrôles réutilisés par les deux rendus (canvas / panel).
  const insertionControls = hasVariables && (
    <InsertionControls
      editorState={editorState}
      setEditorState={setEditorState}
      onAddAt={() => handleAddAt()}
      onAddImage={() => handleAddImageVariable()}
      showLabels={false}
    />
  );

  // Version panel de droite : mêmes contrôles mais avec libellé texte visible en
  // permanence à côté de l'icône (les tooltips seuls ne sont pas assez clairs dans
  // le panel). Empilés en colonne. La barre flottante garde la version icône-seule.
  const panelInsertionControls = hasVariables && (
    <InsertionControls
      editorState={editorState}
      setEditorState={setEditorState}
      onAddAt={() => handleAddAt()}
      onAddImage={() => handleAddImageVariable()}
    />
  );

  const isAlignLeft = boxAlignement === 'left';
  const isAlignCenter = boxAlignement === 'center';
  const isAlignRight = boxAlignement === 'right';
  const isAlignJustify = boxAlignement === 'justify';

  const changeAlignment = (value) => (onAlignmentChange
    ? onAlignmentChange(value)
    : handleAlignmentChange(boxId, updateBox, value));

  const alignmentControls = hasAlignement && (
    <KeepTooltip>
      <NavbarButton
        tooltipContent={t('left-alignment')}
        onClick={() => changeAlignment('left')}
        {...choiceButtonProps(isAlignLeft)}
        icon={<DuotoneLightIcon name='align-left' weight='solid' style={iconButtonStyle(isAlignLeft)} />}
      />
      <NavbarButton
        tooltipContent={t('center-alignment')}
        onClick={() => changeAlignment('center')}
        {...choiceButtonProps(isAlignCenter)}
        icon={<DuotoneLightIcon name='align-center' weight='solid' style={iconButtonStyle(isAlignCenter)} />}
      />
      <NavbarButton
        tooltipContent={t('right-alignment')}
        onClick={() => changeAlignment('right')}
        {...choiceButtonProps(isAlignRight)}
        icon={<DuotoneLightIcon name='align-right' weight='solid' style={iconButtonStyle(isAlignRight)} />}
      />
      <NavbarButton
        tooltipContent={t('justify-alignment')}
        onClick={() => changeAlignment('justify')}
        {...choiceButtonProps(isAlignJustify)}
        icon={<DuotoneLightIcon name='align-justify' weight='solid' style={iconButtonStyle(isAlignJustify)} />}
      />
    </KeepTooltip>
  );

  const currentBlockType = RichUtils.getCurrentBlockType(editorState);
  const isUnorderedList = currentBlockType === 'unordered-list-item';
  const isOrderedList = currentBlockType === 'ordered-list-item';

  const listControls = hasList && (
    <KeepTooltip>
      <NavbarButton
        tooltipContent={t('LISTE_A_PUCE')}
        onClick={() => toggleBlockType('unordered-list-item')}
        {...choiceButtonProps(isUnorderedList)}
        icon={<DuotoneLightIcon name='list-ul' weight='solid' style={iconButtonStyle(isUnorderedList)} />}
      />
      <NavbarButton
        tooltipContent={t('LISTE_NUMEROTEE')}
        onClick={() => {
          toggleBlockType('ordered-list-item');
        }}
        {...choiceButtonProps(isOrderedList)}
        icon={<DuotoneLightIcon name='list-ol' weight='solid' style={iconButtonStyle(isOrderedList)} />}
      />
    </KeepTooltip>
  );

  const fontControls = hasSelectionStyle && (
    <>
      {configuration.fontFamilyList.length > 1 && (
        <FontFamilyControl
          applyFontFamily={applyFontFamily}
          editorState={editorState}
          fontFamily={defaultFontFamily}
          width={wrap ? 84 : 120}
          isOpen={openDropdown === 'fontFamily'}
          onToggleOpen={(isOpen) => setOpenDropdown(isOpen ? 'fontFamily' : null)}
        />
      )}
      {hasFontSize && <FontSizeControl
        editorState={editorState}
        onToggle={applyFontSize}
        defaultFontSize={defaultFontSize ?? configuration.fontSize}
        fontSizes={FONT_SIZES}
        isOpen={openDropdown === 'fontSize'}
        onToggleOpen={(isOpen) => setOpenDropdown(isOpen ? 'fontSize' : null)}
      />}
    </>
  );

  const emphasisControls = hasSelectionStyle && (
    <KeepTooltip>
      {textStyleButtons.map((button) => (
        <NavbarButton
          key={button.title}
          tooltipContent={button.title}
          onClick={toggleInlineState(button.inlineStyleName)}
          {...choiceButtonProps(button.active)}
          icon={
            <p style={{ ...iconButtonStyle(button.active), ...button.style }}>
              {button.textContent}
            </p>}
        />
      ))}
    </KeepTooltip>
  );

  // Couleur du texte + surligneur : isolés de emphasisControls car leur
  // dropdown s'ouvre vers le bas et, dans le panel de droite (colonne étroite,
  // conteneur overflow scroll), passait sous les lignes de boutons suivantes.
  // Rendus sur leur propre ligne, en dernier dans la section "Texte".
  const colorControls = hasSelectionStyle && (
    <KeepTooltip>
      <ColorControl
        editorState={editorState}
        onToggle={applyColor}
        colors={colors}
        defaultColor={defaultColor}
        isOpen={openDropdown === 'color'}
        onToggleOpen={(isOpen) => setOpenDropdown(isOpen ? 'color' : null)}
      />
      <HighlightControl
        editorState={editorState}
        onToggle={applyHighlightcolor}
        colors={configuration.highlightColors}
        isOpen={openDropdown === 'highlight'}
        onToggleOpen={(isOpen) => setOpenDropdown(isOpen ? 'highlight' : null)}
      />
    </KeepTooltip>
  );

  const styleSelector = !!textStyle && (
    <NavbarSelector
      tooltipContent={t('text-style')}
      suffixIcon={<CaretDownOutlined />}
      width={wrap ? '100%' : 120}
      fontSize={wrap ? 11 : undefined}
      onSelect={(e) => {
        updateBox(boxId, (box) => {
          box.border = {};
          box.backgroundColor = {};
          box.content.imagePosition = null;
          box.textStyleId = configuration.textStyles.find((ts) => ts.name === e).id;
        });
      }}
      selectedValue={textStyle?.name}
      dropdownStyle={{ zIndex: 9999 }}
      options={configuration.textStyles.map(style => ({ value: style.name, label: style.name }))}
    />
  );

  // Panel de droite : sections empilées, sans cadre (cf. BoxFormControlsPortal).
  if (wrap) {
    const panelRow = (content) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>{content}</div>
    );
    return (
      <>
        {hasForm && (
          <BoxFormControlsPortal title={t('display')} order={panelOrder}>
            <HideInFormSwitcher boxId={boxId} variant='panel' />
            <VariableSwitcher boxId={boxId} variant='panel' />
          </BoxFormControlsPortal>
        )}
        <BoxFormControlsPortal title={t('text')} order={panelOrder}>
          {fontControls && panelRow(fontControls)}
          {/* Label empilé au-dessus du sélecteur : « Style du champ texte » +
              sélecteur 140px ne tiennent pas côte à côte dans le panel étroit
              (250px). Empilé = label visible + gutter droit respecté. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
            <span style={fieldLabelStyle}>{t('text-style')}</span>
            {styleSelector}
          </div>
          {/* G/I/S + couleur texte + surligneur sur une seule ligne (cohérence
              visuelle) ; le dropdown couleur passe au-dessus via son zIndex. */}
          {hasSelectionStyle && panelRow(<>{emphasisControls}{colorControls}</>)}
          {alignmentControls && panelRow(alignmentControls)}
          {listControls && panelRow(listControls)}
        </BoxFormControlsPortal>
        {panelInsertionControls && (
          <BoxFormControlsPortal title={t('insertion')} order={panelOrder}>
            {panelInsertionControls}
          </BoxFormControlsPortal>
        )}
      </>
    );
  }

  // Barre flottante sur canvas : 2 lignes, séparateurs verticaux.
  return (
    <div className='text-styling-component'
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} tabIndex="0">
      {!!textStyle && (
        <BoxFormControlsPortal>
          {styleSelector}
        </BoxFormControlsPortal>
      )}
      {hasRow1Content && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {insertionControls && <>{insertionControls}<Separator /></>}
          {alignmentControls && <>{alignmentControls}<Separator /></>}
          {listControls}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {hasSelectionStyle && <>
          {fontControls}
          <Separator />
          {emphasisControls}
          {colorControls}
        </>}
      </div>
    </div>
  );
};

export default TextStylingComponent;
